import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, TextArea } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FileUpload } from '../../components/ui/FileUpload';
import { Badge } from '../../components/ui/Badge';
import { useAssessment } from '../../hooks/useAssessment';
import { useGlobalEvidence, GlobalEvidence } from '../../hooks/useGlobalEvidence';
import { useAuth } from '../../hooks/useAuth';
import { EVIDENCE_TYPES, EU_AI_ACT_OBLIGATIONS } from '../../utils/euAiAct';
import { FileText, Plus, Globe, Library, ChevronDown, ChevronUp, Calendar, AlertCircle, X } from 'lucide-react';
export function EvidenceCollection() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    assessment,
    addEvidence
  } = useAssessment();
  const {
    globalEvidence,
    addGlobalEvidence,
    isOverdueForReview
  } = useGlobalEvidence(user?.email || null);
  const [isAdding, setIsAdding] = useState(false);
  const [showGlobalLibrary, setShowGlobalLibrary] = useState(false);
  const [linkingGlobalEvidence, setLinkingGlobalEvidence] = useState<GlobalEvidence | null>(null);
  const [selectedObligationsForGlobal, setSelectedObligationsForGlobal] = useState<string[]>([]);
  // Form State
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState(EVIDENCE_TYPES[0]);
  const [owner, setOwner] = useState('');
  const [relevance, setRelevance] = useState('');
  const [selectedObligations, setSelectedObligations] = useState<string[]>([]);
  const [saveToGlobal, setSaveToGlobal] = useState(false);
  const [globalName, setGlobalName] = useState('');
  const [globalDescription, setGlobalDescription] = useState('');
  const [nextReviewDate, setNextReviewDate] = useState('');
  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    // Auto-populate global name with file name (without extension) when file is selected
    if (saveToGlobal && !globalName) {
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
      setGlobalName(nameWithoutExt);
    }
  };
  const handleObligationToggle = (id: string) => {
    setSelectedObligations(prev => prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]);
  };
  const handleGlobalObligationToggle = (id: string) => {
    setSelectedObligationsForGlobal(prev => prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]);
  };
  const handleSaveToGlobalChange = (checked: boolean) => {
    setSaveToGlobal(checked);
    // Auto-populate name when checkbox is checked
    if (checked && file && !globalName) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setGlobalName(nameWithoutExt);
    }
    // Set default review date to 1 year from now
    if (checked && !nextReviewDate) {
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      setNextReviewDate(oneYearFromNow.toISOString().split('T')[0]);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    const newEvidence = {
      id: crypto.randomUUID(),
      fileName: file.name,
      type,
      owner,
      dateUploaded: new Date().toISOString(),
      relevance,
      linkedObligations: selectedObligations
    };
    // Add to current assessment
    addEvidence(newEvidence);
    // Optionally save to global library
    if (saveToGlobal && globalName) {
      addGlobalEvidence({
        ...newEvidence,
        name: globalName,
        description: globalDescription,
        nextReviewDate: nextReviewDate || undefined,
        linkedObligations: [] // Global evidence starts with no linked obligations
      });
    }
    // Reset form
    setFile(null);
    setOwner('');
    setRelevance('');
    setSelectedObligations([]);
    setSaveToGlobal(false);
    setGlobalName('');
    setGlobalDescription('');
    setNextReviewDate('');
    setIsAdding(false);
  };
  const handleStartLinkingGlobalEvidence = (globalEv: GlobalEvidence) => {
    setLinkingGlobalEvidence(globalEv);
    setSelectedObligationsForGlobal([]);
  };
  const handleLinkGlobalEvidence = () => {
    if (!linkingGlobalEvidence) return;
    // Create a new evidence entry from global evidence with selected obligations
    const newEvidence = {
      id: crypto.randomUUID(),
      fileName: linkingGlobalEvidence.fileName,
      type: linkingGlobalEvidence.type,
      owner: linkingGlobalEvidence.owner,
      dateUploaded: new Date().toISOString(),
      relevance: linkingGlobalEvidence.description || linkingGlobalEvidence.relevance,
      linkedObligations: selectedObligationsForGlobal
    };
    addEvidence(newEvidence);
    setLinkingGlobalEvidence(null);
    setSelectedObligationsForGlobal([]);
  };
  // Filter obligations based on risk level
  const applicableObligations = EU_AI_ACT_OBLIGATIONS.filter(obl => assessment.riskLevel && obl.riskLevel.includes(assessment.riskLevel));
  return <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-245">
      <div className="flex items-center justify-between mb-8" data-id="element-246">
        <div data-id="element-247">
          <h1 className="text-3xl font-bold mb-3" data-id="element-248">Evidence Collection</h1>
          <p className="text-slate-400 text-lg" data-id="element-249">
            Upload and tag documentation to substantiate your compliance.
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-250">
          {globalEvidence.length > 0 && <Button variant="secondary" onClick={() => setShowGlobalLibrary(!showGlobalLibrary)} data-id="element-251">
              <Library className="h-4 w-4 mr-2" data-id="element-252" />
              Global Library ({globalEvidence.length})
            </Button>}
          {!isAdding && <Button onClick={() => setIsAdding(true)} data-id="element-253">
              <Plus className="h-4 w-4 mr-2" data-id="element-254" />
              Add Evidence
            </Button>}
        </div>
      </div>

      {/* Linking Global Evidence Modal */}
      {linkingGlobalEvidence && <Card className="mb-8 border-teal-500 ring-2 ring-teal-500/20 bg-teal-500/5" data-id="element-255">
          <div className="flex items-center justify-between mb-4" data-id="element-256">
            <div className="flex items-center space-x-2" data-id="element-257">
              <Globe className="h-5 w-5 text-teal-500" data-id="element-258" />
              <h3 className="font-semibold text-white" data-id="element-259">
                Link Global Evidence: {linkingGlobalEvidence.name}
              </h3>
            </div>
            <button onClick={() => {
          setLinkingGlobalEvidence(null);
          setSelectedObligationsForGlobal([]);
        }} className="text-slate-400 hover:text-white" data-id="element-260">
              <X className="h-5 w-5" data-id="element-261" />
            </button>
          </div>

          <p className="text-sm text-slate-400 mb-4" data-id="element-262">
            Select which obligations this evidence supports. This will add the
            evidence to your assessment and affect your compliance score.
          </p>

          <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar bg-navy-900 rounded-md p-3 border border-slate-700 mb-4" data-id="element-263">
            {applicableObligations.map(obligation => <label key={obligation.id} className="flex items-start space-x-2 p-2 rounded hover:bg-navy-800 cursor-pointer" data-id="element-264">
                <input type="checkbox" className="mt-1 rounded border-slate-600 bg-navy-900 text-teal-500 focus:ring-teal-500" checked={selectedObligationsForGlobal.includes(obligation.id)} onChange={() => handleGlobalObligationToggle(obligation.id)} data-id="element-265" />
                <span className="text-xs text-slate-300" data-id="element-266">
                  <span className="font-semibold text-teal-400" data-id="element-267">
                    {obligation.id}
                  </span>
                  : {obligation.title}
                </span>
              </label>)}
          </div>

          <div className="flex space-x-3" data-id="element-268">
            <Button onClick={handleLinkGlobalEvidence} disabled={selectedObligationsForGlobal.length === 0} data-id="element-269">
              Add to Assessment
            </Button>
            <Button variant="ghost" onClick={() => {
          setLinkingGlobalEvidence(null);
          setSelectedObligationsForGlobal([]);
        }} data-id="element-270">
              Cancel
            </Button>
          </div>
        </Card>}

      {/* Global Evidence Library */}
      {showGlobalLibrary && globalEvidence.length > 0 && !linkingGlobalEvidence && <Card className="mb-8 border-teal-500/30 bg-teal-500/5" data-id="element-271">
            <div className="flex items-center justify-between mb-4" data-id="element-272">
              <div className="flex items-center space-x-2" data-id="element-273">
                <Globe className="h-5 w-5 text-teal-500" data-id="element-274" />
                <h3 className="font-semibold text-white" data-id="element-275">
                  Global Evidence Library
                </h3>
                <Badge variant="teal" className="text-xs" data-id="element-276">
                  Reusable
                </Badge>
              </div>
              <button onClick={() => setShowGlobalLibrary(false)} className="text-slate-400 hover:text-white" data-id="element-277">
                <ChevronUp className="h-5 w-5" data-id="element-278" />
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4" data-id="element-279">
              Evidence saved to your global library can be reused across all
              assessments.
            </p>
            <div className="space-y-2" data-id="element-280">
              {globalEvidence.map(ev => {
          const isOverdue = isOverdueForReview(ev);
          return <div key={ev.id} className={`flex items-center justify-between p-3 bg-navy-900 rounded-lg border transition-colors ${isOverdue ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-800 hover:border-teal-500/50'}`} data-id="element-281">
                    <div className="flex items-center space-x-3 flex-1 min-w-0" data-id="element-282">
                      <FileText className="h-4 w-4 text-teal-500 flex-shrink-0" data-id="element-283" />
                      <div className="flex-1 min-w-0" data-id="element-284">
                        <div className="flex items-center space-x-2" data-id="element-285">
                          <div className="text-sm font-medium text-white truncate" data-id="element-286">
                            {ev.name}
                          </div>
                          {isOverdue && <Badge variant="amber" className="text-xs flex-shrink-0" data-id="element-287">
                              Review Overdue
                            </Badge>}
                        </div>
                        <div className="flex items-center space-x-2 mt-1 flex-wrap" data-id="element-288">
                          <span className="text-xs text-slate-500" data-id="element-289">
                            {ev.type}
                          </span>
                          {ev.nextReviewDate && <>
                              <span className="text-xs text-slate-600" data-id="element-290">•</span>
                              <span className={`text-xs flex items-center ${isOverdue ? 'text-amber-400' : 'text-slate-400'}`} data-id="element-291">
                                <Calendar className="h-3 w-3 mr-1" data-id="element-292" />
                                Review:{' '}
                                {new Date(ev.nextReviewDate).toLocaleDateString()}
                              </span>
                            </>}
                          {ev.description && <>
                              <span className="text-xs text-slate-600" data-id="element-293">•</span>
                              <span className="text-xs text-slate-400 truncate" data-id="element-294">
                                {ev.description}
                              </span>
                            </>}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleStartLinkingGlobalEvidence(ev)} className="text-teal-400 hover:text-teal-300 flex-shrink-0" data-id="element-295">
                      Link to Obligations
                    </Button>
                  </div>;
        })}
            </div>
          </Card>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-296">
        {/* Left Column: List of Evidence */}
        <div className="lg:col-span-2 space-y-4" data-id="element-297">
          {assessment.evidence.length === 0 && !isAdding ? <Card className="text-center py-12 border-dashed" data-id="element-298">
              <FileText className="h-12 w-12 text-slate-600 mx-auto mb-4" data-id="element-299" />
              <h3 className="text-lg font-medium text-slate-300" data-id="element-300">
                No evidence uploaded yet
              </h3>
              <p className="text-slate-500 mb-6" data-id="element-301">
                Start by adding policies, technical docs, or test results.
              </p>
              <Button onClick={() => setIsAdding(true)} data-id="element-302">
                Upload First Item
              </Button>
            </Card> : assessment.evidence.map(item => <Card key={item.id} className="group hover:border-slate-600 transition-colors" data-id="element-303">
                <div className="flex items-start justify-between" data-id="element-304">
                  <div className="flex items-start space-x-4" data-id="element-305">
                    <div className="h-10 w-10 bg-navy-900 rounded-lg flex items-center justify-center border border-slate-700" data-id="element-306">
                      <FileText className="h-5 w-5 text-teal-500" data-id="element-307" />
                    </div>
                    <div data-id="element-308">
                      <h4 className="text-white font-medium" data-id="element-309">
                        {item.fileName}
                      </h4>
                      <div className="flex items-center space-x-2 mt-1 text-sm text-slate-400" data-id="element-310">
                        <span data-id="element-311">{item.type}</span>
                        <span data-id="element-312">•</span>
                        <span data-id="element-313">{item.owner}</span>
                        <span data-id="element-314">•</span>
                        <span data-id="element-315">
                          {new Date(item.dateUploaded).toLocaleDateString()}
                        </span>
                      </div>
                      {item.linkedObligations.length > 0 && <div className="mt-3 flex flex-wrap gap-2" data-id="element-316">
                          {item.linkedObligations.map(oblId => <Badge key={oblId} variant="slate" className="text-xs" data-id="element-317">
                              {oblId}
                            </Badge>)}
                        </div>}
                    </div>
                  </div>
                </div>
              </Card>)}
        </div>

        {/* Right Column: Upload Form or Summary */}
        <div className="lg:col-span-1" data-id="element-318">
          {isAdding ? <Card className="sticky top-24" data-id="element-319">
              <div className="flex items-center justify-between mb-4" data-id="element-320">
                <h3 className="font-semibold text-white" data-id="element-321">New Evidence</h3>
                <button onClick={() => setIsAdding(false)} className="text-slate-400 hover:text-white" data-id="element-322">
                  <XIcon className="h-5 w-5" data-id="element-323" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-324">
                <FileUpload onFileSelect={handleFileSelect} label={file ? file.name : 'Upload File'} data-id="element-325" />
                {file && <div className="text-sm text-teal-400 flex items-center" data-id="element-326">
                    <FileText className="h-4 w-4 mr-2" data-id="element-327" />
                    {file.name}
                  </div>}

                <Select label="Evidence Type" options={EVIDENCE_TYPES.map(t => ({
              value: t,
              label: t
            }))} value={type} onChange={e => setType(e.target.value)} data-id="element-328" />

                <Input label="Owner" placeholder="e.g. Data Science Team" value={owner} onChange={e => setOwner(e.target.value)} required data-id="element-329" />

                <TextArea label="Relevance / Description" placeholder="How does this support compliance?" rows={2} value={relevance} onChange={e => setRelevance(e.target.value)} required data-id="element-330" />

                <div data-id="element-331">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-332">
                    Linked Obligations
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar" data-id="element-333">
                    {applicableObligations.map(obl => <label key={obl.id} className="flex items-start space-x-2 p-2 rounded hover:bg-navy-900 cursor-pointer" data-id="element-334">
                        <input type="checkbox" className="mt-1 rounded border-slate-600 bg-navy-900 text-teal-500 focus:ring-teal-500" checked={selectedObligations.includes(obl.id)} onChange={() => handleObligationToggle(obl.id)} data-id="element-335" />
                        <span className="text-xs text-slate-300" data-id="element-336">
                          <span className="font-semibold text-teal-400" data-id="element-337">
                            {obl.id}
                          </span>
                          : {obl.title}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Save to Global Library Option */}
                <div className="pt-4 border-t border-slate-800" data-id="element-338">
                  <label className="flex items-start space-x-3 cursor-pointer group" data-id="element-339">
                    <input type="checkbox" checked={saveToGlobal} onChange={e => handleSaveToGlobalChange(e.target.checked)} className="mt-1 rounded border-slate-600 bg-navy-900 text-teal-500 focus:ring-teal-500" data-id="element-340" />
                    <div className="flex-1" data-id="element-341">
                      <div className="flex items-center space-x-2" data-id="element-342">
                        <Globe className="h-4 w-4 text-teal-500" data-id="element-343" />
                        <span className="text-sm font-medium text-white group-hover:text-teal-400 transition-colors" data-id="element-344">
                          Save to Global Library
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1" data-id="element-345">
                        Reuse this evidence across future assessments
                      </p>
                    </div>
                  </label>

                  {saveToGlobal && <div className="mt-3 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-346">
                      <Input label="Evidence Name" placeholder="e.g. Company AI Ethics Policy" value={globalName} onChange={e => setGlobalName(e.target.value)} required data-id="element-347" />
                      <Input label="Next Review Date" type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} data-id="element-348" />
                      <p className="text-xs text-slate-500 -mt-2" data-id="element-349">
                        When should this evidence be reviewed for currency?
                      </p>
                      <TextArea label="Description (Optional)" placeholder="Describe this evidence for future reference..." rows={2} value={globalDescription} onChange={e => setGlobalDescription(e.target.value)} data-id="element-350" />
                    </div>}
                </div>

                <div className="pt-4" data-id="element-351">
                  <Button type="submit" className="w-full" disabled={!file || !owner || saveToGlobal && !globalName} data-id="element-352">
                    Save Evidence
                  </Button>
                </div>
              </form>
            </Card> : <Card className="bg-teal-500/5 border-teal-500/20 sticky top-24" data-id="element-353">
              <h3 className="font-semibold text-teal-400 mb-2" data-id="element-354">
                Ready for Analysis?
              </h3>
              <p className="text-sm text-slate-400 mb-4" data-id="element-355">
                Once you have uploaded sufficient evidence, proceed to the gap
                analysis to see your compliance posture.
              </p>
              <Button className="w-full" onClick={() => navigate('/assessment/analysis')} disabled={assessment.evidence.length === 0} data-id="element-356">
                Run Gap Analysis
              </Button>
            </Card>}
        </div>
      </div>
    </div>;
}
function XIcon({
  className
}: {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-357">
      <line x1="18" y1="6" x2="6" y2="18" data-id="element-358"></line>
      <line x1="6" y1="6" x2="18" y2="18" data-id="element-359"></line>
    </svg>;
}